import { createClient, Entry } from 'contentful-management';

// Define types for environment variables
declare const process: {
  env: {
    REACT_APP_CONTENTFUL_MANAGEMENT_API_ACCESS_TOKEN: string;
    REACT_APP_CONTENTFUL_SPACE_ID: string;
  };
};

// Initialize the Contentful management client
const managementClient = createClient({
  accessToken: process.env.REACT_APP_CONTENTFUL_MANAGEMENT_API_ACCESS_TOKEN,
});

// 🔥 Helper to delete an asset if no other entry references it
const deleteAssetIfUnused = async (assetId: string, environment: any) => {
  try {
    const linkedEntries = await environment.getEntries({ links_to_asset: assetId });

    if (linkedEntries.items.length === 0) {
      let asset;
      try {
        asset = await environment.getAsset(assetId);
      } catch (err: any) {
        if (err?.status === 404) {
          console.log(`Asset ${assetId} already deleted, skipping.`);
          return;
        } else {
          throw err;
        }
      }

      if (asset.isPublished()) await asset.unpublish();
      await asset.delete();
      console.log(`Deleted unused asset with ID: ${assetId}`);
    } else {
      console.log(`Skipped deleting asset ${assetId}, still used in other entries.`);
    }
  } catch (error) {
    console.error(`Failed to check/delete asset with ID: ${assetId}`, error);
  }
};


// 🔥 Collect linked asset IDs from a galleryFolder entry
const getLinkedGalleryAssetIds = (entry: Entry): string[] => {
  const assetIds: string[] = [];

  // Assuming galleryFolder has "images" (array of assets) or "image" (single asset)
  const imageRefs = entry.fields.images?.['en-US'] || entry.fields.image?.['en-US'];

  if (Array.isArray(imageRefs)) {
    imageRefs.forEach((ref) => {
      if (ref.sys?.id && ref.sys?.linkType === 'Asset') {
        assetIds.push(ref.sys.id);
      }
    });
  } else if (imageRefs?.sys?.id && imageRefs?.sys?.linkType === 'Asset') {
    assetIds.push(imageRefs.sys.id);
  }

  return assetIds;
};

// Define the `removeOldEntries` function
export const removeOldEntries = async (): Promise<void> => {
  try {
    const space = await managementClient.getSpace(process.env.REACT_APP_CONTENTFUL_SPACE_ID);
    const environment = await space.getEnvironment('master'); // Replace 'master' if your environment is different

    // Fetch all entries of the 'galleryFolder' content type
    const response = await environment.getEntries({ content_type: 'galleryFolder' });

    const currentDate = new Date();
    const cutoffDate = new Date(currentDate);
    cutoffDate.setDate(cutoffDate.getDate() - 15); // Cutoff date: 15 days ago

    const festivalEntries: Entry[] = [];
    const otherEntries: Entry[] = [];

    response.items.forEach((item: any) => {
      const categoryArray = item.fields.category?.['en-US'];
      if (!Array.isArray(categoryArray)) {
        console.warn(`Skipping entry with invalid category: ${item.sys.id}`);
        return;
      }

      const category = categoryArray[0];
      if (category === 'Festival Darshan') {
        festivalEntries.push(item);
      } else {
        otherEntries.push(item);
      }
    });

    // Handle Festival Darshan: Keep only latest 15
    if (festivalEntries.length > 15) {
      festivalEntries.sort((a, b) => new Date(a.sys.createdAt).getTime() - new Date(b.sys.createdAt).getTime());
      const excessEntries = festivalEntries.slice(0, festivalEntries.length - 15);

      for (const entry of excessEntries) {
        const assetIds = getLinkedGalleryAssetIds(entry);
        try {
          if (entry.isPublished()) await entry.unpublish();
          await entry.delete();
          console.log(`Deleted Festival Darshan entry with ID: ${entry.sys.id}`);

          // After deleting entry, clean up unused assets
          for (const assetId of assetIds) {
            await deleteAssetIfUnused(assetId, environment);
          }
        } catch (error) {
          console.error(`Failed to delete Festival Darshan entry with ID: ${entry.sys.id}`, error);
        }
      }
    }

    // Handle other entries older than cutoff date
    const outdatedEntries = otherEntries.filter((item) => {
      const folderDate = new Date(item.fields.date?.['en-US']);
      return folderDate < cutoffDate;
    });

    for (const entry of outdatedEntries) {
      const assetIds = getLinkedGalleryAssetIds(entry);
      try {
        if (entry.isPublished()) await entry.unpublish();
        await entry.delete();
        console.log(`Deleted outdated entry with ID: ${entry.sys.id}`);

        // After deleting entry, clean up unused assets
        for (const assetId of assetIds) {
          await deleteAssetIfUnused(assetId, environment);
        }
      } catch (error) {
        console.error(`Failed to delete outdated entry with ID: ${entry.sys.id}`, error);
      }
    }
  } catch (error) {
    console.error('Error removing old entries:', error);
  }
};

// Define the `ensureMaxVideos` function
export const ensureMaxVideos = async (maxCount: number = 50): Promise<void> => {
  try {
    const space = await managementClient.getSpace(process.env.REACT_APP_CONTENTFUL_SPACE_ID);
    const environment = await space.getEnvironment('master'); // Replace 'master' if your environment is different

    // Fetch all video entries
    const response = await environment.getEntries({
      content_type: 'kirtansandLectures',
      order: 'sys.createdAt',
    });

    const videos: Entry[] = response.items;

    if (videos.length > maxCount) {
      const excessVideos = videos.slice(0, videos.length - maxCount);

      for (const video of excessVideos) {
        try {
          if (video.isPublished()) await video.unpublish();
          await video.delete();
          console.log(`Deleted video entry with ID: ${video.sys.id}`);
        } catch (error) {
          console.error(`Failed to delete video entry with ID: ${video.sys.id}`, error);
        }
      }
    }
  } catch (error) {
    console.error('Error ensuring max videos:', error);
  }
};
